import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Pencil,
  Plus,
  Trash2,
  Ticket,
  SearchX,
  X,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useBonos } from '@/components/hooks/useBonos';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { CreateBonoDialog } from '@/components/pages/dialogs/CreateBonoDialog';
import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import Pagination from '@/components/pages/shared/Pagination';
import Joyride from 'react-joyride';
import getBonosTourSteps from '@/components/tour/company/BonosTourSteps';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';
import { getTourStyles } from '@/components/tour/tourStyles';

const PAGE_SIZE = 10;

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const rowVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 20, transition: { duration: 0.2 } },
};

type SortKey =
  | 'nombre_bono'
  | 'nombre_servicio'
  | 'descripcion'
  | 'cantidad_servicios'
  | 'precio_total'
  | 'duracion_dias'
  | 'activo';

const safeText = (val: any) => (val ? String(val).toLowerCase() : '');

export default function Bonos() {
  const { t } = useTranslation();
  const empresaId = localStorage.getItem('id_empresa') || '';

  const { bonos, loading, refetch, eliminarBono, crearBono, actualizarBono } =
    useBonos(empresaId, {
      tipo: 'catalogo',
    });

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sortKey, setSortKey] = useState<SortKey>('nombre_bono');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBono, setEditingBono] = useState<any | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [runTour, setRunTour] = useState(false);
  const isMobile = window.innerWidth < 640;

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta
  // página. Se oculta mientras carga, que es cuando aún no hay tabla que
  // enseñar.
  usePageHelp(() => setRunTour(true), !loading);
  useTourVisibility(runTour);

  const steps = getBonosTourSteps(isMobile);

  const filtered = bonos.filter((b) => {
    const q = filter.toLowerCase();
    return (
      safeText(b.nombre_bono).includes(q) ||
      safeText(b.nombre_servicio).includes(q) ||
      safeText(b.descripcion).includes(q) ||
      safeText(b.precio_total).includes(q) ||
      safeText(b.cantidad_servicios).includes(q) ||
      safeText(b.duracion_dias).includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    let aVal: any, bVal: any;

    switch (sortKey) {
      case 'nombre_bono':
        aVal = a.nombre_bono;
        bVal = b.nombre_bono;
        break;
      case 'nombre_servicio':
        aVal = a.nombre_servicio;
        bVal = b.nombre_servicio;
        break;
      case 'descripcion':
        aVal = a.descripcion || '';
        bVal = b.descripcion || '';
        break;
      case 'cantidad_servicios':
        aVal = a.cantidad_servicios ?? 0;
        bVal = b.cantidad_servicios ?? 0;
        break;
      case 'precio_total':
        aVal = a.precio_total ?? 0;
        bVal = b.precio_total ?? 0;
        break;
      case 'duracion_dias':
        aVal = a.duracion_dias ?? 0;
        bVal = b.duracion_dias ?? 0;
        break;
      case 'activo':
        aVal = a.activo ? 1 : 0;
        bVal = b.activo ? 1 : 0;
        break;
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / pageSize) || 1;
  const currentData = sorted.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (key: SortKey) =>
    sortKey === key ? (sortOrder === 'asc' ? '▲' : '▼') : null;

  const openCreateModal = () => {
    setEditingBono(null);
    setOpenDialog(true);
  };
  const openEditModal = (b: any) => {
    setEditingBono(b);
    setOpenDialog(true);
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-8 pb-28 lg:pb-0"
    >
      {/* HEADER — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('bonos.title')}
          </span>
        </div>
      </motion.div>

      {/* TOUR */}
      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: t('profiles.tour.back'),
          close: t('profiles.tour.close'),
          last: t('profiles.tour.last'),
          next: t('profiles.tour.next'),
          skip: t('profiles.tour.skip'),
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {/* FILTRO + AÑADIR */}
      <motion.div
        variants={itemVariants}
        className="flex flex-col sm:flex-row gap-3 justify-between items-center"
      >
        <Input
          type="text"
          placeholder={t('bonos.searchPlaceholder')}
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1);
          }}
          className="tour-filter-input w-full sm:w-1/2 dark:bg-white/5 dark:border-white/10 dark:text-white"
        />

        {bonos.length > 0 && (
          <Button
            onClick={openCreateModal}
            className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 tour-add-btn w-full sm:w-auto transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" /> {t('bonos.addBono')}
          </Button>
        )}
      </motion.div>

      {/* DIALOG CREAR/EDITAR */}
      <CreateBonoDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        editingBono={editingBono}
        onSaved={async () => {
          await refetch();
          setOpenDialog(false);
        }}
        crearBono={crearBono}
        actualizarBono={actualizarBono}
      />

      {/* DIALOG CONFIRMAR ELIMINACIÓN (bottom sheet) */}
      <Dialog
        open={!!confirmDeleteId}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
      >
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-red-600 dark:text-red-400 pr-10">
                {t('bonos.deleteTitle')}
              </DialogTitle>
            </DialogHeader>
            <button
              onClick={() => setConfirmDeleteId(null)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('bonos.deleteText')}
              </p>
            </div>
          </div>

          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteId(null)}
              className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
            >
              {t('bonos.cancel')}
            </Button>
            <Button
              onClick={async () => {
                const ok = await eliminarBono(confirmDeleteId!);
                if (ok) toast.success(t('bonos.deleteSuccess'));
                else toast.error(t('bonos.deleteError'));
                setConfirmDeleteId(null);
              }}
              className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
            >
              {t('bonos.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TABLA / ESTADOS */}
      <AnimatePresence>
        {loading ? (
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center h-32"
          >
            <motion.div
              variants={rowVariants}
              className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#103254]"
            />
          </motion.div>
        ) : bonos.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <Ticket className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                {t('bonos.noBonos')}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">
                {t('bonos.noBonosText')}
              </p>
              <Button
                onClick={openCreateModal}
                className="rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] px-6 py-2 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('bonos.addBono')}
              </Button>
            </CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <SearchX className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                {t('bonos.noResults')}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {t('bonos.noResultsText')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Vista móvil - Tarjetas */}
            <div className="block lg:hidden space-y-4">
              {currentData.map((b) => (
                <motion.div
                  key={b.id_bono}
                  variants={rowVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Card className={`rounded-xl ${GLASS_CARD}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-base">
                            {b.nombre_bono}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {b.nombre_servicio}
                          </p>
                          {b.descripcion && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {b.descripcion}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditModal(b)}
                            className="text-[#103254] dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-full tour-edit-btn-mobile hover:bg-gray-100 dark:hover:bg-white/10"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={b.tiene_ventas}
                            onClick={() => setConfirmDeleteId(b.id_bono)}
                            title={
                              b.tiene_ventas
                                ? 'No se puede eliminar: el bono ya ha sido vendido'
                                : 'Eliminar bono'
                            }
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 rounded-full tour-delete-btn-mobile disabled:opacity-30 disabled:cursor-not-allowed hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            {t('bonos.cardQuantity')}
                          </span>
                          <p className="font-medium text-gray-800 dark:text-white">
                            {b.cantidad_servicios}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            {t('bonos.cardPrice')}
                          </span>
                          <p className="font-medium text-gray-800 dark:text-white">
                            {b.precio_total != null
                              ? `${b.precio_total.toFixed(2)} €`
                              : '-'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            {t('bonos.cardDuration')}
                          </span>
                          <p className="font-medium text-gray-600 dark:text-gray-300">
                            {b.duracion_dias} {t('bonos.days')}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Vista desktop - TABLA */}
            <Card className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}>
              <CardContent className="overflow-x-auto p-0">
                <table className="min-w-full table-auto text-sm">
                  <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                    <tr>
                      <th
                        onClick={() => handleSort('nombre_bono')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        {t('bonos.colName')} {renderSortIcon('nombre_bono')}
                      </th>
                      <th
                        onClick={() => handleSort('nombre_servicio')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        {t('bonos.colService')}{' '}
                        {renderSortIcon('nombre_servicio')}
                      </th>
                      <th
                        onClick={() => handleSort('descripcion')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        {t('bonos.colDescription')}{' '}
                        {renderSortIcon('descripcion')}
                      </th>
                      <th
                        onClick={() => handleSort('cantidad_servicios')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        {t('bonos.colQuantity')}{' '}
                        {renderSortIcon('cantidad_servicios')}
                      </th>
                      <th
                        onClick={() => handleSort('precio_total')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        {t('bonos.colPrice')} {renderSortIcon('precio_total')}
                      </th>
                      <th
                        onClick={() => handleSort('duracion_dias')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        {t('bonos.colDuration')}{' '}
                        {renderSortIcon('duracion_dias')}
                      </th>
                      <th
                        onClick={() => handleSort('activo')}
                        className="px-4 py-3 text-left cursor-pointer hover:bg-gray-200 dark:hover:bg-white/10 transition"
                      >
                        {t('bonos.colActive')} {renderSortIcon('activo')}
                      </th>
                      <th className="px-4 py-3 text-left w-24">
                        {t('bonos.colActions')}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                    {currentData.map((b, idx) => (
                      <motion.tr
                        key={b.id_bono}
                        variants={rowVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className={
                          idx % 2 === 0
                            ? 'bg-white dark:bg-transparent'
                            : 'bg-gray-50 dark:bg-white/[0.03]'
                        }
                      >
                        <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">
                          {b.nombre_bono}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {b.nombre_servicio}
                        </td>
                        <td
                          className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-[250px] truncate"
                          title={b.descripcion || ''}
                        >
                          {b.descripcion || '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-800 dark:text-white">
                          {b.cantidad_servicios}
                        </td>
                        <td className="px-4 py-3 text-gray-800 dark:text-white">
                          {b.precio_total != null
                            ? `${b.precio_total.toFixed(2)} €`
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {b.duracion_dias} {t('bonos.days')}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold tour-activo-badge ${
                              b.activo
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            }`}
                          >
                            {b.activo
                              ? t('bonos.activeYes')
                              : t('bonos.activeNo')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(b)}
                              className="text-[#103254] dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-full tour-edit-btn-desktop hover:bg-gray-100 dark:hover:bg-white/10"
                              aria-label={t('bonos.editAria')}
                            >
                              <Pencil className="w-5 h-5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={b.tiene_ventas}
                              onClick={() => setConfirmDeleteId(b.id_bono)}
                              title={
                                b.tiene_ventas
                                  ? 'No se puede eliminar: el bono ya ha sido vendido'
                                  : t('bonos.deleteAria')
                              }
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 rounded-full tour-delete-btn-desktop disabled:opacity-30 disabled:cursor-not-allowed hover:bg-red-50 dark:hover:bg-red-900/20"
                              aria-label={t('bonos.deleteAria')}
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* PAGINACIÓN */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={sorted.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={changePageSize}
              itemLabel="bonos"
              className="tour-pagination"
            />
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
}